import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import useScrollLock from '../../hooks/useScrollLock';
import { useLanguage } from '../../contexts/LanguageContext';
import { useSalon } from '../../contexts/SalonContext';
import { printBulkDressDeclarations } from '../../utils/printBulkDeclarations';
import DressDisplay from './DressDisplay';
import './Salon.css';

const BulkPrintDeclarationsModal = ({ isOpen, onClose, salonId, salon }) => {
  const { t, getMonthName } = useLanguage();
  const { formatPrice } = useSalon();

  const [paperSize, setPaperSize] = useState('A4');
  const [declarations, setDeclarations] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  const [dressSearchTerm, setDressSearchTerm] = useState('');
  const [dressSearchResults, setDressSearchResults] = useState([]);
  const [isSearchingDresses, setIsSearchingDresses] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const searchTimeoutRef = useRef(null);

  const [selectedDresses, setSelectedDresses] = useState([]);
  const [printing, setPrinting] = useState(false);
  const [error, setError] = useState('');

  useScrollLock(isOpen);

  const fetchDeclarations = useCallback(async () => {
    if (!salonId) return;
    try {
      setLoadingTemplates(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/salons/${salonId}/declarations?active_only=true&limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const list = response.data.declarations || [];
      setDeclarations(list);
      if (list.length > 0) {
        setSelectedTemplateId(String(list[0].id));
      } else {
        setSelectedTemplateId('');
      }
    } catch (err) {
      console.error('Error fetching declaration templates:', err);
      setError(t('messages.error.failedToFetchDeclarationTemplates', 'Failed to load declaration templates'));
    } finally {
      setLoadingTemplates(false);
    }
  }, [salonId, t]);

  useEffect(() => {
    if (isOpen) {
      setError('');
      setPaperSize('A4');
      setSelectedDresses([]);
      setDressSearchTerm('');
      setDressSearchResults([]);
      setShowSearchResults(false);
      fetchDeclarations();
    }
  }, [isOpen, fetchDeclarations]);

  const searchDresses = async (searchTerm) => {
    if (searchTerm.trim().length < 2) {
      setDressSearchResults([]);
      return;
    }

    setIsSearchingDresses(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/salons/${salonId}/dresses?search=${encodeURIComponent(searchTerm)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDressSearchResults(response.data.dresses || []);
    } catch (err) {
      console.error('Error searching dresses:', err);
      setDressSearchResults([]);
    } finally {
      setIsSearchingDresses(false);
    }
  };

  const handleDressSearchChange = (e) => {
    const value = e.target.value;
    setDressSearchTerm(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (value.trim().length >= 2) {
      setShowSearchResults(true);
      searchTimeoutRef.current = setTimeout(() => {
        searchDresses(value);
      }, 300);
    } else {
      setDressSearchResults([]);
      setShowSearchResults(false);
    }
  };

  const addDress = (dress) => {
    setSelectedDresses((prev) => {
      if (prev.some((d) => d.id === dress.id)) return prev;
      return [...prev, dress];
    });
    setDressSearchTerm('');
    setDressSearchResults([]);
    setShowSearchResults(false);
  };

  const removeDress = (dressId) => {
    setSelectedDresses((prev) => prev.filter((d) => d.id !== dressId));
  };

  const handlePrint = async () => {
    if (!selectedTemplateId) {
      setError(t('messages.error.selectDeclarationFirst', 'Please select a declaration template'));
      return;
    }
    if (selectedDresses.length === 0) {
      setError(t('dresses.noDressesSelectedForPrint', 'Add at least one dress to print'));
      return;
    }

    try {
      setPrinting(true);
      setError('');

      await printBulkDressDeclarations({
        salonId,
        templateId: selectedTemplateId,
        dresses: selectedDresses,
        paperSize,
        t,
        formatPrice,
        getMonthName,
        salonSlug: salon?.slug
      });
      onClose();
    } catch (err) {
      console.error('Error printing declarations:', err);
      setError(err.message || t('messages.error.failedToPrintDeclaration', 'Failed to print declaration'));
    } finally {
      setPrinting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: '560px' }}>
        <div className="modal-header">
          <h3>{t('dresses.printDeclarations', 'Print declarations')}</h3>
          <button type="button" className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {error && <div className="error-message">{error}</div>}

          <div className="form-group">
            <label htmlFor="bulk-print-paper-size">{t('dresses.paperSize', 'Paper size')}</label>
            <select
              id="bulk-print-paper-size"
              value={paperSize}
              onChange={(e) => setPaperSize(e.target.value)}
              disabled={printing}
            >
              <option value="A4">A4</option>
              <option value="letter">Letter</option>
            </select>
          </div>

          {loadingTemplates ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>{t('dresses.loadingDeclaration', 'Loading templates...')}</p>
            </div>
          ) : declarations.length === 0 ? (
            <p>{t('dresses.noDeclarationTemplates', 'No active declaration templates. Create one in Settings.')}</p>
          ) : (
            <div className="form-group">
              <label htmlFor="bulk_declaration_template_select">{t('dresses.selectDeclarationTemplate', 'Declaration template')}</label>
              <select
                id="bulk_declaration_template_select"
                value={selectedTemplateId}
                onChange={(e) => setSelectedTemplateId(e.target.value)}
                disabled={printing}
              >
                {declarations.map((decl) => (
                  <option key={decl.id} value={decl.id}>{decl.title}</option>
                ))}
              </select>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="bulk_dress_search">{t('dresses.searchDress', 'Search dress...')}</label>
            <div className="email-search-container">
              <input
                type="text"
                id="bulk_dress_search"
                value={dressSearchTerm}
                onChange={handleDressSearchChange}
                placeholder={t('dresses.searchDress', 'Search dress...')}
                className="email-input"
                autoComplete="off"
                disabled={printing}
              />
              {isSearchingDresses && (
                <div className="search-spinner">
                  <div className="spinner"></div>
                </div>
              )}
            </div>

            {showSearchResults && dressSearchResults.length > 0 && (
              <div className="search-results">
                {dressSearchResults.map((dress) => (
                  <div
                    key={dress.id}
                    className="search-result-item"
                    onClick={() => addDress(dress)}
                  >
                    <DressDisplay
                      dress={dress}
                      imageClassName="dress-thumbnail"
                      showStockCount={dress.stock_count === 0}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {selectedDresses.length > 0 && (
            <div className="bulk-dress-list">
              {selectedDresses.map((dress) => (
                <div key={dress.id} className="bulk-dress-list-item">
                  <DressDisplay dress={dress} imageClassName="table-preview-image" showBrand={false} />
                  <button
                    type="button"
                    className="bulk-dress-remove-btn"
                    onClick={() => removeDress(dress.id)}
                    title={t('common.remove')}
                    aria-label={t('common.remove')}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button type="button" className="salon-button secondary" onClick={onClose} disabled={printing}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="salon-button primary"
            onClick={handlePrint}
            disabled={printing || loadingTemplates || declarations.length === 0}
          >
            {printing ? t('dresses.loadingDeclaration', 'Loading...') : t('dresses.printDeclarations', 'Print declarations')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkPrintDeclarationsModal;
